import { useCallback, useEffect, useRef, useState } from 'react';

import { loadGooglePlaces } from 'utils/googleMapsLoader';

/**
 * Supplies Google address predictions for a text input, under explicit control
 * of how many billable requests that costs.
 *
 * The address search previously used Google's own Autocomplete widget, which
 * offers no control at all: it issued a prediction request on every keystroke,
 * and on each selection it also fetched Place Details. Place Details is the
 * most expensive Places SKU, and the app consumed exactly one field of the
 * response -- the formatted address, which each prediction already carries. The
 * platform was buying the premium call and discarding it.
 *
 * Three rules bound the spend here:
 *   - nothing is requested until the query is long enough to be an address
 *   - keystrokes are debounced, so a typed address costs a few requests, not one per character
 *   - a selection resolves from the prediction already in hand, so Place Details is never called
 *
 * Predictions are also biased to New York City, which is the only place the
 * platform has data for. Better predictions arrive sooner, so a user types
 * fewer characters and issues fewer requests.
 */

/**
 * Milliseconds of typing quiet before a prediction request is issued.
 */
const DEBOUNCE_MS = 350;

/**
 * Shortest query worth asking Google about.
 *
 * A one- or two-character query cannot identify a building, so the predictions
 * are useless while still being billable.
 */
const MIN_QUERY_LENGTH = 4;

/**
 * Viewport predictions are biased toward, covering the five boroughs.
 */
const NYC_BOUNDS = {
	south: 40.4774,
	west: -74.2591,
	north: 40.9176,
	east: -73.7002,
};

/**
 * Returns predictions for a query string, plus the session controls a selection
 * needs.
 *
 * @param {string} query The current text typed into the search input
 * @returns {{predictions: Array, loading: boolean, ready: boolean, clear: Function, startNewSession: Function}}
 */
export default function useAddressPredictions(query) {
	const [predictions, setPredictions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [ready, setReady] = useState(false);

	const serviceRef = useRef(null);
	const sessionTokenRef = useRef(null);
	const timerRef = useRef(null);
	// Guards against a slow response for an earlier query overwriting the
	// predictions for a later one.
	const requestIdRef = useRef(0);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		loadGooglePlaces()
			.then(() => {
				if (!mountedRef.current) {
					return;
				}
				serviceRef.current = new window.google.maps.places.AutocompleteService();
				setReady(true);
			})
			.catch(() => {
				// A failed load leaves the input as a plain text field rather than
				// breaking the page: the address can still be typed and submitted.
				if (mountedRef.current) {
					setReady(false);
				}
			});

		return () => {
			mountedRef.current = false;
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, []);

	/**
	 * Begins a new autocomplete session.
	 *
	 * Note what this does and does not do for the bill. Google only collapses a
	 * run of prediction requests into one charged session when that session ends
	 * in a Place Details call. This component deliberately never makes one, so
	 * every prediction request here is billed individually and the token changes
	 * nothing about the price today.
	 *
	 * It is still sent, for two reasons: it is what groups the requests correctly
	 * should a Place Details call ever be reintroduced, and it keeps the requests
	 * of one search identifiable to Google rather than looking like unrelated
	 * one-off lookups. Because the token does not discount anything, the debounce
	 * and the minimum query length above are what actually control the cost.
	 *
	 * @returns {void}
	 */
	const startNewSession = useCallback(() => {
		if (window.google && window.google.maps && window.google.maps.places) {
			sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
		}
	}, []);

	/**
	 * Drops the current predictions without issuing a request.
	 *
	 * @returns {void}
	 */
	const clear = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		requestIdRef.current += 1;
		setPredictions([]);
		setLoading(false);
	}, []);

	useEffect(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		const trimmed = (query || '').trim();

		// Too short to identify a building: spend nothing.
		if (!ready || trimmed.length < MIN_QUERY_LENGTH) {
			requestIdRef.current += 1;
			setPredictions([]);
			setLoading(false);
			return undefined;
		}

		setLoading(true);

		timerRef.current = setTimeout(() => {
			if (!serviceRef.current) {
				return;
			}

			if (!sessionTokenRef.current) {
				startNewSession();
			}

			requestIdRef.current += 1;
			const requestId = requestIdRef.current;

			serviceRef.current.getPlacePredictions(
				{
					input: trimmed,
					types: ['address'],
					componentRestrictions: { country: 'us' },
					locationBias: NYC_BOUNDS,
					sessionToken: sessionTokenRef.current,
				},
				(results) => {
					// Ignore a response that a newer keystroke has already superseded.
					if (!mountedRef.current || requestId !== requestIdRef.current) {
						return;
					}
					setPredictions(Array.isArray(results) ? results : []);
					setLoading(false);
				},
			);
		}, DEBOUNCE_MS);

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [query, ready, startNewSession]);

	/**
	 * Ends the current session once a selection has been made, so the next
	 * search is billed as its own session.
	 *
	 * @returns {void}
	 */
	const endSession = useCallback(() => {
		sessionTokenRef.current = null;
	}, []);

	return { predictions, loading, ready, clear, startNewSession, endSession };
}
